import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";
import mindfulmeIcon from "@/assets/mindfulme-icon.png";

const Navigation = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navItems = [
    { name: "Beranda", href: "#hero" },
    { name: "Artikel", href: "/news", isRoute: true },
    { name: "Kontak", href: "#contact" },
  ];

  interface VolunteerNavItem {
    name: string;
    href: string;
  }

  const volunteerNavItems: VolunteerNavItem[] = [];

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
      isScrolled 
        ? "bg-white/80 dark:bg-slate-900/80 backdrop-blur-lg border-b border-primary/10 py-3 shadow-md" 
        : "bg-white/90 dark:bg-slate-900/40 backdrop-blur-md border-b border-white/10 py-5"
    }`}>
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-12">
          {/* Logo */}
          <div className="flex items-center space-x-3 group cursor-pointer">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-white/80 shadow-md group-hover:scale-105 transition-transform duration-300">
              <img src={mindfulmeIcon} alt="MindfulMe Icon" className="w-8 h-8 object-contain" />
            </div>
            <span className="text-xl font-extrabold tracking-tight text-primary bg-clip-text hover:opacity-90 transition-opacity">
              MindfulMe
            </span>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8 ml-auto mr-4">
            {navItems.map((item) =>
              item.isRoute ? (
                <Link
                  key={item.name}
                  to={item.href}
                  className="relative text-sm font-semibold text-foreground/80 hover:text-primary transition-colors duration-300 py-2 group"
                >
                  {item.name}
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary transition-all duration-300 group-hover:w-full"></span>
                </Link>
              ) : (
                <a
                  key={item.name}
                  href={isHome ? item.href : `/${item.href}`}
                  className="relative text-sm font-semibold text-foreground/80 hover:text-primary transition-colors duration-300 py-2 group"
                >
                  {item.name}
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary transition-all duration-300 group-hover:w-full"></span>
                </a>
              )
            )}
          </div>

          {/* Volunteer Actions */}
          <div className="hidden md:flex items-center space-x-4">
            {volunteerNavItems.map((item) => (
              <Button
                key={item.name}
                variant="hero"
                size="sm"
                onClick={() => window.open(item.href, '_blank')}
              >
                {item.name}
              </Button>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden p-2 rounded-lg bg-white/60 backdrop-blur-sm border border-border/40 hover:bg-white transition-colors"
          >
            {isMenuOpen ? <X className="w-5 h-5 text-foreground" /> : <Menu className="w-5 h-5 text-foreground" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden mt-4 pt-4 border-t border-border/40 animate-slide-up">
            <div className="flex flex-col space-y-3 bg-white/95 backdrop-blur-md p-4 rounded-2xl shadow-xl border border-border/20">
              {navItems.map((item) =>
                item.isRoute ? (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setIsMenuOpen(false)}
                    className="text-foreground/85 hover:text-primary transition-colors duration-200 py-2 font-medium px-2 rounded-lg hover:bg-primary/5"
                  >
                    {item.name}
                  </Link>
                ) : (
                  <a
                    key={item.name}
                    href={isHome ? item.href : `/${item.href}`}
                    onClick={() => setIsMenuOpen(false)}
                    className="text-foreground/85 hover:text-primary transition-colors duration-200 py-2 font-medium px-2 rounded-lg hover:bg-primary/5"
                  >
                    {item.name}
                  </a>
                )
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;